/**
 * ── THE ASSET SERVICE — the one runtime object of the library track (plan §4; ADR-0003 §4) ───────
 *
 * `acquire(id, { lane })` turns a catalog id into a ref-counted HANDLE at any moment — boot,
 * mid-play, editor, after a purchase — and returns it SYNCHRONOUSLY. Everything after that is the
 * pure core's arithmetic driven by this module's I/O:
 *
 *   catalog (core)     id → asset, deps flattened, the capability gate
 *   scheduler (core)   which fetch starts next, per lane, under an injected concurrency
 *   ledger (core)      refcount per SOURCE (`id@hash`), grace period, eviction order
 *   readiness (core)   which handles gate `playing` (boot lane only)
 *   loaders            bytes → a parsed GLB / clips / a Texture, with the resources tracked
 *   instantiate        a fresh clone per `handle.instance()`
 *
 * The ONE place a warning becomes a `console.warn`, once per key (rule 6). The ONE place an
 * exception becomes a FAILURE CODE: an AbortError is `aborted`, a 404/410 is `missing`, any other
 * transport/parse fault is `network`, an integrity mismatch is `network` (the bytes on the wire
 * are wrong — the CDN's fault, not the catalog's), a kind/format/class this runtime lacks is
 * `unsupported`, and a prefetch over the ceiling is `budget`.
 *
 * ── ⭐ P1.5 · THE TWO-LEVEL CACHE (plan §13.2) ───────────────────────────────────────────────────
 * Three granularities exist and conflating any two is how this rots: the ENTITY is the unit of
 * DECLARATION, the FILE is the unit of TRANSFER, the parsed SOURCE is the unit of LIFETIME. P1
 * keyed transfer to the declaration, so byte-identical content under two ids shipped twice. Now:
 *
 *   BLOB      keyed by `sha256`. Owns the in-flight FETCH and the DECODED resource (a parsed GLB,
 *             a decoded `Texture`, an `AnimationClip[]`, a rig object) and the GPU objects behind
 *             it. Refcounted by the ASSET SOURCES that reference it. Two assets naming one blob
 *             are ONE download and ONE upload; the accountant charges it ONCE, and `stats().blobs`
 *             is what says so.
 *   SOURCE    keyed by `(id, hash)`, exactly as P1. Holds BLOB REFS and composes them into the
 *             `AssetSource` a handle settles on. It owns no GPU object of its own.
 *
 * DISPOSAL WALKS DOWN: asset refcount 0 → the ledger's grace period → `tick()` discards the source
 * → its blob refs are released → a blob at refcount 0 disposes its GPU objects immediately. The
 * grace lives at the ASSET level only; a blob whose last asset expired has, by construction,
 * already waited out that grace.
 * ⭐ AND A `material` SOURCE HOLDS BLOB REFS TOO, THOUGH IT HAS NO FILE (`holdTextureBlobs`): the
 * `Texture` objects its `Material` samples are the textures' BLOBS, so a lifetime that lived only
 * in the acquiring handle's dep closure now lives in the record that memoized them.
 *
 * ⚠️ ONE DECODE PER BLOB, AND A CONFLICT IS A REFUSAL RATHER THAN A LIE. The decode of a blob
 * depends on more than its bytes (a PNG's colour space comes from the asset's labels), so each
 * blob records the `decodeKey` of the asset that first decoded it. A second asset wanting the SAME
 * bytes decoded DIFFERENTLY is `failed('unsupported')` with both asset ids named — never a silent
 * first-decode-wins, which would be a wrong-looking texture with every counter green. It cannot
 * fire on today's 23 assets (no two share a blob at all) and it is named in the plan's record.
 *
 * ── LIFETIMES ───────────────────────────────────────────────────────────────────────────────────
 * A SOURCE is created on the first acquire of `id@hash` and its blobs are fetched once however
 * many handles attach (two acquires share one fetch). When its last handle releases:
 *   · still fetching → the fetch is ABORTED at once and the source discarded (nothing to keep);
 *   · ready → the ledger's grace period starts (default 5 s, injected clock); `tick()` disposes
 *     it after that, and a re-acquire inside the window cancels the disposal (no upload thrash).
 * `tick()` is the ONE disposal trigger and it is also called at the end of every acquire/release,
 * so a caller that never ticks still converges; mount calls it per frame (stage C).
 * ⚠️ `id@hash` is an ADDRESS, not a lifetime — a failed fetch drops its entry while its handles
 * are still alive, so the same address can name a SECOND source in one session. Every ledger
 * write on the release path is therefore fenced on RECORD IDENTITY, never on the key (see
 * `releaseHandle`). ⭐ THE SAME DISCIPLINE NOW RUNS AT BOTH LEVELS: `sha256` is an address too,
 * a failed blob is dropped from the table so a later acquire retries, and `releaseBlobRef` fences
 * on `blobs.get(b.sha256) === b` before it touches a refcount — otherwise a stale source's
 * teardown would decrement a LIVE blob 1 → 0 and dispose a texture something is still sampling.
 *
 * ── VRAM (§4.4, §13.2) ──────────────────────────────────────────────────────────────────────────
 * ⭐ THE ACCOUNTANT CHARGES THE BLOB, ONCE. The catalog's `vramEstimateMB` is per ASSET, so it is
 * APPORTIONED across that asset's blobs by byte share at `fetching` and CORRECTED at `ready` to
 * the census's own arithmetic over the decoded resource (`censusScene`: texture + geometry bytes;
 * a decoded image is `sizeOf`; clips and a rig are 0). Every MB figure the service reports — the
 * ledger's, `stats().mb`, the budget projection and the amount an eviction frees — is summed over
 * BLOBS, so two assets sharing bytes are two residents over one charge. Charging per asset was
 * arithmetically identical while every blob had exactly one owner and would have double-counted
 * the first time one did not. One ceiling (`prefetchBudgetMB`, plan §11 q2's tier-0 default) is
 * enforced at acquire time: a `prefetch` acquire that would exceed it first evicts IDLE prefetch
 * residents oldest-released-first and is REFUSED (`budget`) if that is not enough; `boot` and
 * `gameplay` evict the same way and are NEVER refused. A held source is never evicted from under
 * a live handle — a magenta placeholder appearing mid-play is the failure that rule prevents.
 *
 * ── DEPENDENCIES ────────────────────────────────────────────────────────────────────────────────
 * A root handle acquires one CHILD handle per transitive dep (the core's flattened, deduplicated
 * load order), in the same lane, released with it. The root settles `ready` only when its own
 * source AND every child are ready; a child that fails makes the root `failed('missing')`.
 *
 * ── PURITY BOUNDARY ─────────────────────────────────────────────────────────────────────────────
 * This module reads NO clock of its own (`opts.clock`), performs NO fetch of its own
 * (`opts.fetch`), hashes through an injected `verifier` (WebCrypto by default), decodes images
 * through an injected decoder, and warns through an injected `warn`. That is what lets every
 * lifetime claim below be a Node test with a fake clock rather than a claim about a browser.
 */

import { censusScene, sizeOf, type TextureLike, type VramAccountant } from '../render/assets/vramAccountant';
import { decodeKeyFor } from './core/decode';
import {
  KIND_REQUIRED_ROLES,
  ENTITY_PREFAB_KIND,
  MATERIAL_INHERITING_KIND,
  checkAssetRequires,
  emptyCatalog,
  mergeCatalogs,
  parseCatalog,
  parseCatalogPointer,
  resolveAsset,
  resolveEntityPrefab,
  resolveMaterial,
  type AssetFile,
  type AssetKind,
  type LibraryAsset,
  type LibraryCatalog,
} from './core/catalog';
import {
  acquire as ledgerAcquire,
  createLedger,
  evict as ledgerEvict,
  evictionOrder,
  expire,
  ledgerKey,
  ledgerTotals,
  release as ledgerRelease,
  setVram,
  type LedgerEntry,
  type LedgerRef,
  type LedgerState,
} from './core/ledger';
import { bootGate, handleCounters, type BootGateReport, type HandleCounters, type HandleView } from './core/readiness';
import {
  DEFAULT_SCHEDULER_CONFIG,
  cancel as schedulerCancel,
  complete as schedulerComplete,
  createScheduler,
  drain,
  enqueue,
  prune,
  schedulerCounts,
  type SchedulerState,
} from './core/scheduler';
import type { AssetLane, FailureCode, HandleFailure } from './core/types';
import { blobUrl, catalogPointerUrl, catalogUrl } from './core/uri';
import {
  createHandle,
  type AssetHandle,
  type AssetSource,
  type ClipSource,
  type HandleController,
  type MaterialSource,
  type MeshSource,
  type RigSidecar,
  type SourceOf,
  type TextureSource,
} from './handle';
import { buildMaterialSource, instantiate } from './instantiate';
import { parseClips } from './loaders/clip';
import { disposeTracked, parseGltf } from './loaders/gltf';
import { decodeTexture, mimeForFormat, textureColorSpaceOf, textureRoleOf, type ImageDecoder } from './loaders/texture';

// ── Injected seams ─────────────────────────────────────────────────────────────────────────────

/** The slice of `Response` the service reads — the real `fetch` is assignable. */
export interface FetchResponseLike {
  ok: boolean;
  status: number;
  statusText?: string;
  arrayBuffer(): Promise<ArrayBuffer>;
  json(): Promise<unknown>;
}

export type FetchLike = (url: string, init?: { signal?: AbortSignal }) => Promise<FetchResponseLike>;

/** The accountant seam, in BYTES. `vramAccountantAdapter` wraps the real `VramAccountant`. */
export interface LibraryVram {
  charge(owner: object, bytes: number): void;
  credit(owner: object): void;
}

/** sha256 over bytes → lowercase hex. Default: WebCrypto `subtle.digest` (browsers and Node ≥ 20). */
export type Sha256Verifier = (bytes: ArrayBuffer) => Promise<string>;

export interface DisposedSource {
  id: string;
  hash: string;
  kind: AssetKind;
  /** `expired` after the grace period · `evicted` for budget · `aborted` before ready · `dispose` on service teardown. */
  why: 'expired' | 'evicted' | 'aborted' | 'dispose';
  /** The blobs this source was the LAST holder of — i.e. the ones whose GPU objects it just freed. */
  blobsFreed: number;
}

/** One row per blob the service actually disposed — the level the GPU objects live at (§13.2). */
export interface DisposedBlob {
  sha256: string;
  ext: string;
  vramMB: number;
  why: DisposedSource['why'];
}

/** The transfer level's row in `stats()` (§13.2: "blobs resident alongside assets"). */
export interface BlobStats {
  /** Blobs holding a decoded resource or bytes in flight. */
  resident: number;
  /** Blobs queued, fetching or decoding. */
  inflight: number;
  /** The truth about residency: every blob's measured (or, before decode, apportioned) MB, ONCE. */
  mb: number;
  /** Sum of `refs` over resident blobs — how many asset sources point at them. */
  refs: number;
  /** Blob loads that started while another asset had already paid for the same bytes. */
  shared: number;
  disposed: number;
  failed: number;
}

export interface AssetServiceOptions {
  fetch: FetchLike;
  /** `''` = same-origin; else `https://host` — from `resolveLibraryOrigin`. */
  origin: string;
  /** Override for the POINTER url (`/library/catalog/latest.json`); tests and `?assets=<url>` use it. */
  catalogUrl?: string;
  /** Milliseconds, monotonic — the ledger's grace period runs on it. `performance.now` in the app. */
  clock: () => number;
  vram?: LibraryVram;
  /** `Object.keys(ENTITY_REGISTRY)` — read at acquire time so a late sub-table still counts. */
  registryClasses: () => ReadonlySet<string>;
  /** Decoder formats this runtime has: `meshopt` today; `ktx2` when R2 lands. */
  formats: ReadonlySet<string>;
  graceMs?: number;
  laneLimits?: Partial<Record<AssetLane, number>>;
  /** Per-load sha256 (dev `?assets=verify`). Off in production — the hashed immutable path is the integrity story. */
  verify?: boolean;
  verifier?: Sha256Verifier;
  /** The ONE residency ceiling (MB). Plan §11 q2: tier-0 32, tier-2 128. */
  prefetchBudgetMB?: number;
  decodeImage?: ImageDecoder;
  /** Default `console.warn`. Every message goes through here ONCE per key. */
  warn?: (message: string) => void;
  /** Observability for tests and the dev hook — fires once per disposed ASSET source. */
  onDispose?: (info: DisposedSource) => void;
  /** …and once per disposed BLOB, which is where the GPU objects actually are (§13.2). */
  onDisposeBlob?: (info: DisposedBlob) => void;
}

export interface AcquireOptions<K extends AssetKind = AssetKind> {
  /** Default `gameplay`. */
  lane?: AssetLane;
  /** Assert the catalog's kind; a mismatch is `failed('unsupported')`, never a wrong-typed instance. */
  kind?: K;
}

export interface CatalogLoadResult {
  ok: boolean;
  /** The pointer's hash (primary) — `null` for a secondary or a refusal. */
  hash: string | null;
  assets: number;
  warnings: string[];
  error: string | null;
}

/** The dev-hook row (`window.__fwg.library`, plan §4.4/§8) — stage C wires it. */
export interface LibraryStats {
  /** Resident sources — held or idle. */
  resident: number;
  /** Sources queued, fetching or parsing. */
  inflight: number;
  /** The ledger's MB (estimates until `ready`, measured after). */
  mb: number;
  evictions: number;
  disposed: number;
  aborted: number;
  failed: number;
  byLane: Record<AssetLane, number>;
  scheduler: { queued: number; active: number };
  handles: HandleCounters;
  /** ⭐ The TRANSFER level. `resident` here is ≤ `resident` above whenever dedup has fired. */
  blobs: BlobStats;
  catalog: { hash: string | null; assets: number; secondaries: number };
}

export interface AssetService {
  /** Pointer → catalog. Never throws; a refusal is `{ ok: false, error }` and the old catalog stands. */
  loadCatalog(url?: string): Promise<CatalogLoadResult>;
  /** A SECONDARY catalog (a season drop) merged by id — a later id wins only if its version is newer. */
  addCatalog(url: string): Promise<CatalogLoadResult>;
  /** Inject a parsed catalog as the primary (tests; an inline dev catalog). Secondaries already added still merge over it. */
  useCatalog(catalog: LibraryCatalog): void;
  /** The merged view every acquire resolves against. */
  catalog(): LibraryCatalog;
  /**
   * ── ⭐ P1 · THE ONE PLACE THE CDN LAYOUT IS SPELLED FOR A CALLER THAT FETCHES ITS OWN BYTES ────
   *
   * A synchronous `id → URL` over the merged catalog, for the seam that already speaks in URLs:
   * `world/assets.resolveAssetUrl`'s `lib:` arm, which mount wires to THIS function. It is the
   * whole of the `lib:` integration and it deliberately does not acquire — a `static_mesh`
   * placement owns its own fetch, its own abort and its own collision walk, and handing it a
   * handle would put two lifetimes on one mesh.
   *
   * `role` defaults to the kind's FIRST required role (`mesh` for a mesh/body, `clip`, `image`),
   * which is the file a URL-shaped caller means. `null` — never a throw — when the catalog does
   * not have the id, or the entry does not have the role.
   *
   * ⭐ P1.5: what comes back is the BLOB url (`/library/blobs/<sha256>.<ext>`), so two ids naming
   * one blob hand their callers ONE url and the browser cache does the rest.
   */
  fileUrl(id: string, role?: string): string | null;
  acquire<K extends AssetKind = AssetKind>(id: string, opts?: AcquireOptions<K>): AssetHandle<K>;
  /** Same as `handle.release()`. */
  release(handle: AssetHandle): void;
  /** Dispose sources whose grace period has elapsed at `clock()`. Returns how many. Call per frame. */
  tick(): number;
  stats(): LibraryStats;
  /** Every live handle (root and dependency) as a readiness view. */
  handles(): HandleView[];
  /** `readiness.bootGate` over `handles()` — what mount ANDs with collider residency. */
  bootGate(): BootGateReport;
  /** Abort everything in flight, dispose every source, forget every handle. */
  dispose(): void;
}

// ── Internals ──────────────────────────────────────────────────────────────────────────────────

const MiB = 1024 * 1024;

/**
 * The kinds this runtime instantiates. P1 shipped four; P1.5·B1 added `material` (plan §13.4) and
 * P1.5·C adds `entity` (§13.6). `skinned` is P2 and `audio` P3 — `failed('unsupported')` until
 * then, which is a STATE (ADR-0003 decision 3), not a gap in the enum.
 */
const V1_KINDS: ReadonlySet<AssetKind> = new Set<AssetKind>(['mesh', 'body', 'clip', 'texture', 'material', 'entity']);

type SourceState = 'queued' | 'fetching' | 'parsing' | 'ready' | 'failed';
type BlobState = 'queued' | 'fetching' | 'decoding' | 'ready' | 'failed';

/**
 * What ONE blob's bytes become. Deliberately NOT `AssetSource`: a source is COMPOSED from decoded
 * blobs (a body is one `gltf` plus one `json`), and it is the blob that owns the GPU objects and
 * the disposal walk.
 */
type DecodedBlob =
  | { kind: 'gltf'; scene: MeshSource['scene']; skinned: boolean; tracked: MeshSource['tracked'] }
  | { kind: 'clips'; clips: ClipSource['clips'] }
  | { kind: 'texture'; texture: TextureSource['texture'] }
  | { kind: 'json'; value: RigSidecar };

interface BlobRecord {
  sha256: string;
  ext: string;
  format: string;
  /** The catalog's declared size — the apportioning denominator. */
  bytes: number;
  /** What these bytes were decoded AS. A second, DIFFERENT request is refused (see the header). */
  decodeKey: string;
  /** The asset that established `decodeKey` — named in the refusal so both sides are visible. */
  decodedFor: string;
  decode: (bytes: ArrayBuffer) => Promise<DecodedBlob>;
  state: BlobState;
  decoded: DecodedBlob | null;
  failure: HandleFailure | null;
  abort: AbortController;
  /** Asset SOURCES referencing it. Zero ⇒ dispose, with no grace: the asset level already waited. */
  refs: number;
  users: Set<SourceRecord>;
  /** The accountant's owner key — ONE per blob, credited on every exit path. */
  owner: object;
  vramMB: number;
}

interface SourceRecord {
  key: string;
  id: string;
  hash: string;
  kind: AssetKind;
  asset: LibraryAsset;
  state: SourceState;
  /** Composed from `blobs`' decodes. Owns NO GPU object — the blobs do. */
  source: AssetSource | null;
  failure: HandleFailure | null;
  handles: Set<HandleController<AssetKind>>;
  /**
   * role → the blob carrying it, in required-role order. These are the refs this source holds, and
   * `discard` releases every one of them. ⭐ A MATERIAL HAS NO FILE AND STILL APPEARS HERE: it
   * appends one `texture:<slot>` entry per BOUND slot when it composes (`holdTextureBlobs`), so
   * the textures its `Material` samples outlive the handle that acquired it.
   */
  blobs: { role: string; blob: BlobRecord }[];
}

interface HandleLink {
  record: SourceRecord | null;
  ref: LedgerRef | null;
  children: HandleController<AssetKind>[];
}

class AbortedError extends Error {
  override readonly name = 'AbortError';
}
/** A 404/410, a role the entry lacks, a dep the catalog lacks. */
class MissingError extends Error {}
/** A kind, class or decoder this runtime does not have. */
class UnsupportedError extends Error {}

const isAbort = (err: unknown): boolean =>
  err instanceof AbortedError || (err instanceof Error && err.name === 'AbortError');

/** Rows the accountant prices exactly: `sizeOf({ image: { data: { byteLength } }, generateMipmaps: false })` is `byteLength`. */
export function vramAccountantAdapter(acc: VramAccountant): LibraryVram {
  return {
    charge(owner, bytes) {
      if (bytes <= 0) return;
      const row: TextureLike = { image: { data: { byteLength: bytes } }, generateMipmaps: false };
      acc.charge(owner, row);
    },
    credit(owner) {
      acc.credit(owner);
    },
  };
}

const defaultVerifier: Sha256Verifier = async (bytes) => {
  const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
  if (subtle === undefined) throw new Error('WebCrypto subtle.digest is not available — inject `verifier`');
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Bytes the census would charge for ONE decoded blob — the `ready` correction (§4.4), per blob. */
function measureDecoded(decoded: DecodedBlob): number {
  switch (decoded.kind) {
    case 'gltf': {
      const c = censusScene(decoded.scene);
      return c.textureBytes + c.geometryBytes;
    }
    case 'texture':
      // r185 types `Texture.image` as `unknown`; the accountant duck-types it (the `scene.ts` cast).
      return sizeOf(decoded.texture as unknown as TextureLike);
    case 'clips':
    case 'json':
      return 0;
  }
}

/** The disposal walk, at the level that owns the objects. */
function disposeDecoded(decoded: DecodedBlob): void {
  switch (decoded.kind) {
    case 'gltf':
      disposeTracked(decoded.tracked);
      break;
    case 'texture':
      decoded.texture.dispose();
      break;
    case 'clips':
    case 'json':
      break;
  }
}

/** The file roles a kind must fetch, in the order the parser wants them. */
function rolesFor(kind: AssetKind): readonly string[] {
  return KIND_REQUIRED_ROLES[kind];
}

/** The (role, blob) pairs an asset actually transfers — its REQUIRED roles, present ones only. */
function requiredFiles(asset: LibraryAsset): { role: string; file: AssetFile }[] {
  const out: { role: string; file: AssetFile }[] = [];
  for (const role of rolesFor(asset.kind)) {
    const file = asset.files[role];
    if (file !== undefined) out.push({ role, file });
  }
  return out;
}

/** The catalog's per-ASSET estimate, split across its blobs by byte share (§13.2's VRAM note). */
function apportion(assetMB: number, blobBytes: number, totalBytes: number): number {
  return totalBytes <= 0 ? assetMB : assetMB * (blobBytes / totalBytes);
}

export function createAssetService(opts: AssetServiceOptions): AssetService {
  const warned = new Set<string>();
  const warn = opts.warn ?? ((m: string) => console.warn(m));
  const warnOnce = (key: string, message: string): void => {
    if (warned.has(key)) return;
    warned.add(key);
    warn(message);
  };

  const verifier = opts.verifier ?? defaultVerifier;
  const verify = opts.verify === true;
  const budgetMB = opts.prefetchBudgetMB ?? 32;

  let primary: LibraryCatalog = emptyCatalog();
  let primaryHash: string | null = null;
  const secondaries: LibraryCatalog[] = [];
  let merged: LibraryCatalog = primary;
  const rebuildMerged = (): void => {
    merged = mergeCatalogs(primary, ...secondaries);
  };

  const ledger: LedgerState = createLedger({ graceMs: opts.graceMs });
  const scheduler: SchedulerState = createScheduler({
    concurrency: { ...DEFAULT_SCHEDULER_CONFIG.concurrency, ...(opts.laneLimits ?? {}) },
  });
  const sources = new Map<string, SourceRecord>();
  /** ⭐ THE TRANSFER LEVEL, keyed by sha256 (§13.2). The scheduler's request ids are these keys. */
  const blobs = new Map<string, BlobRecord>();
  const links = new Map<HandleController<AssetKind>, HandleLink>();
  const counters = {
    evictions: 0,
    disposed: 0,
    aborted: 0,
    failed: 0,
    blobsDisposed: 0,
    blobsFailed: 0,
    blobsShared: 0,
  };
  let disposed = false;

  // ── catalogs ──────────────────────────────────────────────────────────────────────────────

  const fetchJson = async (url: string): Promise<{ ok: true; json: unknown } | { ok: false; error: string }> => {
    try {
      const res = await opts.fetch(url);
      if (!res.ok) return { ok: false, error: `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''} for ${url}` };
      return { ok: true, json: await res.json() };
    } catch (err) {
      return { ok: false, error: `${url}: ${err instanceof Error ? err.message : String(err)}` };
    }
  };

  const refuse = (error: string): CatalogLoadResult => {
    warnOnce(`catalog:${error}`, `[assets] ${error}`);
    return { ok: false, hash: null, assets: 0, warnings: [], error };
  };

  const fetchCatalogAt = async (
    url: string,
  ): Promise<{ ok: true; catalog: LibraryCatalog; warnings: string[] } | { ok: false; error: string }> => {
    const got = await fetchJson(url);
    if (!got.ok) return got;
    const parsed = parseCatalog(got.json);
    if (!parsed.ok) return { ok: false, error: `${parsed.error} (${url})` };
    for (const w of parsed.warnings) warnOnce(`catalog-warning:${w}`, `[assets] catalog: ${w}`);
    return { ok: true, catalog: parsed.catalog, warnings: parsed.warnings };
  };

  const loadCatalog = async (url?: string): Promise<CatalogLoadResult> => {
    const pointer = url ?? opts.catalogUrl ?? catalogPointerUrl(opts.origin);
    const got = await fetchJson(pointer);
    if (!got.ok) return refuse(`catalog pointer unavailable — ${got.error}`);
    const ptr = parseCatalogPointer(got.json);
    if (!ptr.ok) return refuse(`${ptr.error} (${pointer})`);
    const cat = await fetchCatalogAt(catalogUrl(opts.origin, ptr.hash));
    if (!cat.ok) return refuse(`catalog unavailable — ${cat.error}`);
    primary = cat.catalog;
    primaryHash = ptr.hash;
    rebuildMerged();
    return { ok: true, hash: ptr.hash, assets: Object.keys(cat.catalog.assets).length, warnings: cat.warnings, error: null };
  };

  const addCatalog = async (url: string): Promise<CatalogLoadResult> => {
    const cat = await fetchCatalogAt(url);
    if (!cat.ok) return refuse(`secondary catalog unavailable — ${cat.error}`);
    secondaries.push(cat.catalog);
    rebuildMerged();
    return { ok: true, hash: null, assets: Object.keys(cat.catalog.assets).length, warnings: cat.warnings, error: null };
  };

  const useCatalog = (catalog: LibraryCatalog): void => {
    primary = catalog;
    primaryHash = null;
    rebuildMerged();
  };

  // ── blobs — the TRANSFER level (§13.2) ────────────────────────────────────────────────────

  const parseRig = (buf: ArrayBuffer): RigSidecar => {
    const text = new TextDecoder().decode(buf);
    const json: unknown = JSON.parse(text);
    if (json === null || typeof json !== 'object' || Array.isArray(json)) throw new Error('rig.json is not an object');
    return json as RigSidecar;
  };

  const classify = (err: unknown): HandleFailure => {
    if (isAbort(err)) return { code: 'aborted', message: 'released before it was ready' };
    const message = err instanceof Error ? err.message : String(err);
    if (err instanceof MissingError) return { code: 'missing', message };
    if (err instanceof UnsupportedError) return { code: 'unsupported', message };
    return { code: 'network', message };
  };

  /**
   * What ONE (asset, role) wants a blob decoded AS, plus the decoder itself. Two callers agreeing
   * on the KEY share the decode; two disagreeing over one sha256 is the refusal the module header
   * describes.
   *
   * ⭐ THE KEY IS `core/decode.decodeKeyFor` AND IS NOT SPELLED HERE. It reads only catalog data
   * (kind, role, `format`, and the `map-*` / `colourspace-*` labels — a PNG's bytes carry no
   * colour space, and `axonRig`'s measured facts live in the labels), which is exactly why
   * `LibraryCatalogSchema` can now REFUSE a catalog that reads one blob two ways, naming both
   * asset ids before a byte ships. A second spelling here would let the publish tool and this
   * runtime disagree about which catalogs are legal, so there is one function and this is a
   * caller. What survives here is the runtime guard for a SECONDARY catalog merged at runtime,
   * which no publish ever saw.
   */
  const decoderFor = (
    asset: LibraryAsset,
    role: string,
    file: AssetFile,
  ): { key: string; decode: (bytes: ArrayBuffer) => Promise<DecodedBlob> } => {
    const key = decodeKeyFor(asset, role, file);
    if (role === 'rig') {
      return { key, decode: (bytes) => Promise.resolve<DecodedBlob>({ kind: 'json', value: parseRig(bytes) }) };
    }
    if (asset.kind === 'clip') {
      return {
        key,
        decode: async (bytes) => {
          const parsed = await parseClips(bytes);
          if (parsed.discardedMeshes > 0) {
            warnOnce(
              `unpacked-clip:${file.sha256}`,
              `[assets] clip blob ${file.sha256.slice(0, 12)}… carried ${parsed.discardedMeshes} preview mesh(es) — ` +
                'discarded; pack it (cook:asset) to stop paying for them',
            );
          }
          return { kind: 'clips', clips: parsed.clips };
        },
      };
    }
    if (asset.kind === 'texture') {
      const colorSpace = textureColorSpaceOf(asset);
      const texRole = textureRoleOf(asset);
      return {
        key,
        decode: async (bytes) => ({
          kind: 'texture',
          texture: await decodeTexture(bytes, {
            format: file.format,
            role: texRole,
            colorSpace,
            ...(opts.decodeImage !== undefined ? { decodeImage: opts.decodeImage } : {}),
          }),
        }),
      };
    }
    return {
      key,
      decode: async (bytes) => {
        const parsed = await parseGltf(bytes);
        return { kind: 'gltf', scene: parsed.scene, skinned: parsed.skinned, tracked: parsed.tracked };
      },
    };
  };

  /** Every resident blob's MB, ONCE — the truth about residency, and what `stats().mb` reports. */
  const blobVramMB = (): number => {
    let mb = 0;
    for (const b of blobs.values()) mb += b.vramMB;
    return mb;
  };

  /** The asset ledger's `vramMB` is an ATTRIBUTION for eviction ordering; `mb` is summed over blobs. */
  const attributeVram = (rec: SourceRecord): void => {
    let mb = 0;
    for (const { blob } of rec.blobs) mb += blob.vramMB;
    setVram(ledger, { id: rec.id, hash: rec.hash }, mb);
  };

  /**
   * Take a reference on the blob for `(asset, role)`, creating and enqueuing it on first touch.
   * `null` — never a throw — when that sha256 is already decoded a DIFFERENT way; the caller turns
   * that into `failed('unsupported')` naming both assets.
   */
  const acquireBlob = (
    rec: SourceRecord,
    role: string,
    file: AssetFile,
    lane: AssetLane,
    estimateMB: number,
  ): BlobRecord | null => {
    const want = decoderFor(rec.asset, role, file);
    let blob = blobs.get(file.sha256);
    if (blob === undefined) {
      blob = {
        sha256: file.sha256,
        ext: file.ext,
        format: file.format,
        bytes: file.bytes,
        decodeKey: want.key,
        decodedFor: rec.id,
        decode: want.decode,
        state: 'queued',
        decoded: null,
        failure: null,
        abort: new AbortController(),
        refs: 0,
        users: new Set(),
        owner: {},
        vramMB: estimateMB,
      };
      blobs.set(file.sha256, blob);
      enqueue(scheduler, { id: file.sha256, lane });
      opts.vram?.charge(blob.owner, estimateMB * MiB);
    } else {
      if (blob.decodeKey !== want.key) return null;
      counters.blobsShared += 1;
      if (blob.state === 'queued') enqueue(scheduler, { id: file.sha256, lane });
    }
    blob.refs += 1;
    blob.users.add(rec);
    rec.blobs.push({ role, blob });
    return blob;
  };

  /**
   * Drop one reference. ⚠️ FENCED ON RECORD IDENTITY, exactly as `releaseHandle` is: `sha256` is
   * an ADDRESS, and a failed blob is dropped from the table while the sources that asked for it
   * are still alive. Decrementing by key would take a LATER, LIVE blob at the same address 1 → 0
   * and dispose GPU objects something is drawing from. Returns whether it freed the blob.
   */
  const releaseBlobRef = (rec: SourceRecord, blob: BlobRecord, why: DisposedSource['why']): boolean => {
    blob.users.delete(rec);
    if (blobs.get(blob.sha256) !== blob) return false;
    blob.refs -= 1;
    if (blob.refs > 0) return false;
    discardBlob(blob, why);
    return true;
  };

  /** Take a blob out of the table, abort what is in flight, free what it holds, credit the charge. */
  const discardBlob = (blob: BlobRecord, why: DisposedSource['why']): void => {
    if (blobs.get(blob.sha256) === blob) blobs.delete(blob.sha256);
    schedulerCancel(scheduler, blob.sha256);
    blob.abort.abort();
    opts.vram?.credit(blob.owner);
    const vramMB = blob.vramMB;
    blob.vramMB = 0;
    if (blob.decoded !== null) {
      disposeDecoded(blob.decoded);
      blob.decoded = null;
      counters.blobsDisposed += 1;
      opts.onDisposeBlob?.({ sha256: blob.sha256, ext: blob.ext, vramMB, why });
    }
  };

  // ── handles ───────────────────────────────────────────────────────────────────────────────

  const views = (): HandleView[] => {
    const out: HandleView[] = [];
    for (const ctrl of links.keys()) {
      out.push({ id: ctrl.handle.id, lane: ctrl.handle.lane, state: ctrl.handle.state });
    }
    return out;
  };

  /** A handle that settles `failed` synchronously — the catalog said no before any byte moved. */
  const failedHandle = <K extends AssetKind>(
    id: string,
    kind: K,
    lane: AssetLane,
    hash: string | null,
    code: FailureCode,
    message: string,
  ): AssetHandle<K> => {
    const ctrl = createHandle<K>({
      id,
      kind,
      lane,
      hash,
      instantiate,
      onRelease: (c) => releaseHandle(c as HandleController<AssetKind>),
      warnOnce,
    });
    links.set(ctrl as HandleController<AssetKind>, { record: null, ref: null, children: [] });
    counters.failed += 1;
    warnOnce(`${code}:${id}:${message}`, `[assets] '${id}' ${code}: ${message}`);
    ctrl.fail(code, message);
    return ctrl.handle;
  };

  /**
   * Free residency for `neededMB` by disposing IDLE sources, prefetch-lane residents first. Never
   * touches a held source. What it reports FREED is measured over BLOBS actually disposed, not
   * over the asset's attribution — evicting an asset whose blob another asset still holds frees
   * nothing, and saying otherwise would let the ceiling be exceeded on a lie.
   */
  const evictIdleFor = (neededMB: number): number => {
    const before = blobVramMB();
    let freed = 0;
    const candidates: LedgerEntry[] = [
      ...evictionOrder(ledger, { lanes: ['prefetch'] }),
      ...evictionOrder(ledger, { lanes: ['gameplay', 'boot'] }),
    ];
    for (const e of candidates) {
      if (freed >= neededMB) break;
      if (e.count !== 0) continue;
      const rec = sources.get(e.key);
      ledgerEvict(ledger, e);
      if (rec !== undefined) discard(rec, 'evicted');
      counters.evictions += 1;
      freed = before - blobVramMB();
    }
    return freed;
  };

  const acquire = <K extends AssetKind = AssetKind>(id: string, acquireOpts: AcquireOptions<K> = {}): AssetHandle<K> => {
    const lane = acquireOpts.lane ?? 'gameplay';
    const resolved = resolveAsset(merged, id);
    for (const w of resolved.warnings) warnOnce(`resolve:${w}`, `[assets] ${w}`);
    if (!resolved.found || resolved.asset === null) {
      // The kind is unknowable for an id the catalog lacks; the handle reports what was asked for.
      const kind = (acquireOpts.kind ?? 'mesh') as K;
      return failedHandle(id, kind, lane, null, 'missing', 'not in the library catalog');
    }
    const asset = resolved.asset;
    const kind = asset.kind as K;
    if (acquireOpts.kind !== undefined && acquireOpts.kind !== asset.kind) {
      return failedHandle(
        id,
        acquireOpts.kind,
        lane,
        asset.hash,
        'unsupported',
        `expected kind '${acquireOpts.kind}', the catalog says '${asset.kind}'`,
      );
    }
    if (!V1_KINDS.has(asset.kind)) {
      return failedHandle(id, kind, lane, asset.hash, 'unsupported', `kind '${asset.kind}' is not instantiable in this runtime yet`);
    }
    for (const role of rolesFor(asset.kind)) {
      if (!(role in asset.files)) {
        return failedHandle(id, kind, lane, asset.hash, 'missing', `kind '${asset.kind}' needs a '${role}' file and the catalog entry has none`);
      }
    }
    const verdict = checkAssetRequires(resolved, { classes: opts.registryClasses(), formats: opts.formats });
    if (!verdict.supported) {
      const parts: string[] = [];
      if (verdict.missingClasses.length > 0) parts.push(`classes ${verdict.missingClasses.join(', ')}`);
      if (verdict.missingFormats.length > 0) parts.push(`formats ${verdict.missingFormats.join(', ')}`);
      return failedHandle(id, kind, lane, asset.hash, 'unsupported', `this runtime lacks ${parts.join(' and ')}`);
    }
    if (asset.kind === 'texture') {
      const image = asset.files['image'];
      if (image !== undefined && mimeForFormat(image.format) === null) {
        return failedHandle(id, kind, lane, asset.hash, 'unsupported', `texture format '${image.format}' has no decoder in this runtime (KTX2 is R2)`);
      }
    }
    if (resolved.missing.length > 0) {
      return failedHandle(id, kind, lane, asset.hash, 'missing', `depends on '${resolved.missing.join("', '")}', which the catalog does not carry`);
    }

    /*
     * The residency ceiling (§4.4), PROJECTED AT THE BLOB LEVEL (§13.2). What a new acquire costs
     * is the estimate of the blobs it introduces that are not already resident — so a second
     * handle on a resident `id@hash` costs zero (only the refcount moves), and so does a second
     * ASSET naming a blob the first one already pulled in. That second case is the whole point of
     * the scheme and an asset-level projection cannot see it: it would refuse a prefetch that
     * costs nothing, which is the one budget refusal that can never be right. An IDLE blob inside
     * its holder's grace period counts as resident, because its bytes are.
     */
    const pending = new Set<string>();
    let newMB = 0;
    const project = (a: LibraryAsset): void => {
      const files = requiredFiles(a);
      const total = files.reduce((n, f) => n + f.file.bytes, 0);
      for (const { file } of files) {
        if (blobs.has(file.sha256) || pending.has(file.sha256)) continue;
        pending.add(file.sha256);
        newMB += apportion(a.vramEstimateMB, file.bytes, total);
      }
    };
    project(asset);
    for (const depId of resolved.deps) {
      const dep = merged.assets[depId];
      if (dep !== undefined) project(dep);
    }
    const projectedMB = blobVramMB() + newMB;
    if (projectedMB > budgetMB) {
      const freed = evictIdleFor(projectedMB - budgetMB);
      if (lane === 'prefetch' && projectedMB - freed > budgetMB) {
        return failedHandle(
          id,
          kind,
          lane,
          asset.hash,
          'budget',
          `prefetch refused: ${(projectedMB - freed).toFixed(1)} MB would exceed the ${budgetMB} MB library ceiling`,
        );
      }
    }

    const ctrl = attach(id, asset, lane, resolved.deps) as HandleController<K>;
    tick();
    return ctrl.handle;
  };

  /** Attach a handle to the source for `asset`, creating it and taking its blob refs on first touch. */
  const attach = (id: string, asset: LibraryAsset, lane: AssetLane, deps: readonly string[]): HandleController<AssetKind> => {
    const ref: LedgerRef = { id, hash: asset.hash };
    const key = ledgerKey(id, asset.hash);
    const ctrl = createHandle<AssetKind>({
      id,
      kind: asset.kind,
      lane,
      hash: asset.hash,
      instantiate,
      onRelease: releaseHandle,
      warnOnce,
    });
    const link: HandleLink = { record: null, ref, children: [] };
    links.set(ctrl, link);

    /*
     * ⚠️ NO `vramMB` IS PASSED HERE ANY MORE, AND THAT CLOSES P1'S OVERWRITE HAZARD BY REMOVING
     * ITS SUBJECT. `ledger.acquire` writes `vramMB` whenever it is handed one, so P1 had to pass
     * the catalog's estimate only on the FIRST acquire or a re-acquire would overwrite the
     * MEASURED figure with the guess (Axon's body estimates 1.2 MB and measures ~8). The ledger's
     * figure is now DERIVED — `attributeVram` folds the source's blobs — so there is no path by
     * which an estimate can outrank a measurement.
     */
    ledgerAcquire(ledger, ref, opts.clock(), { lane });

    let rec = sources.get(key);
    if (rec === undefined) {
      rec = {
        key,
        id,
        hash: asset.hash,
        kind: asset.kind,
        asset,
        state: 'queued',
        source: null,
        failure: null,
        handles: new Set(),
        blobs: [],
      };
      sources.set(key, rec);
      const files = requiredFiles(asset);
      const total = files.reduce((n, f) => n + f.file.bytes, 0);
      let conflict: string | null = null;
      for (const { role, file } of files) {
        const held = blobs.get(file.sha256);
        const blob = acquireBlob(rec, role, file, lane, apportion(asset.vramEstimateMB, file.bytes, total));
        if (blob === null) {
          conflict =
            `blob ${file.sha256.slice(0, 12)}… is already decoded for '${held?.decodedFor ?? '?'}' as ` +
            `'${held?.decodeKey ?? '?'}', which is not how role '${role}' of a '${asset.kind}' reads it`;
          break;
        }
      }
      if (conflict !== null) {
        // Structural refusal: drop the half-built source and every ref it took, and fail the
        // handle. NOT a throw and NOT a silent first-decode-wins (rule 6; the header says why).
        ledgerEvict(ledger, ref);
        discard(rec, 'aborted');
        link.record = null;
        link.ref = null;
        counters.failed += 1;
        warnOnce(`decode-conflict:${key}`, `[assets] '${id}' unsupported: ${conflict}`);
        ctrl.fail('unsupported', conflict);
        return ctrl;
      }
      attributeVram(rec);
    } else {
      // A more urgent lane promotes the queued fetches; the scheduler decides.
      for (const { blob } of rec.blobs) if (blob.state === 'queued') enqueue(scheduler, { id: blob.sha256, lane });
    }
    rec.handles.add(ctrl);
    link.record = rec;

    // One CHILD handle per transitive dep (already flattened + deduplicated by the core), no
    // further expansion: the closure is the root's, so a child's own deps are already in `deps`.
    for (const depId of deps) {
      const dep = merged.assets[depId];
      if (dep === undefined) continue;
      link.children.push(attach(depId, dep, lane, []));
    }

    syncSource(rec);
    syncHandle(ctrl, link);
    for (const child of link.children) {
      void child.handle.ready.then(() => {
        if (!ctrl.settled && !ctrl.released) syncHandle(ctrl, link);
      });
    }
    pump();
    return ctrl;
  };

  /**
   * Fold a source's BLOBS into its own state, composing the `AssetSource` once they are all
   * decoded. THE ONE PLACE THE TWO LEVELS MEET: below it a blob knows nothing about assets, above
   * it a handle knows nothing about blobs.
   */
  const syncSource = (rec: SourceRecord): void => {
    if (sources.get(rec.key) !== rec || rec.state === 'ready' || rec.state === 'failed') return;
    if (rec.kind === MATERIAL_INHERITING_KIND) {
      /*
       * ⭐ A MATERIAL HAS NO BLOBS, SO THERE IS NOTHING HERE TO FOLD (plan §13.4).
       * `KIND_REQUIRED_ROLES.material` is `[]` — its definition is catalog data, not a file — so
       * its TRANSFER is complete the moment it is attached. What it still waits on is its texture
       * DEPS, which are a different granularity: `materialSourceOf` composes it in `syncHandle`,
       * which is the one place that knows when they have landed.
       */
      setSourceState(rec, 'ready');
      return;
    }
    const failed = rec.blobs.find((b) => b.blob.state === 'failed');
    if (failed !== undefined) {
      setSourceFailed(rec, failed.blob.failure ?? { code: 'network', message: 'blob load failed' });
      return;
    }
    const decodes = new Map<string, DecodedBlob>();
    for (const { role, blob } of rec.blobs) {
      if (blob.state !== 'ready' || blob.decoded === null) {
        // Not composable yet: the source's state is the least-advanced of its blobs.
        let state: SourceState = 'parsing';
        for (const { blob: b } of rec.blobs) {
          if (b.state === 'queued') state = 'queued';
          else if (b.state === 'fetching' && state !== 'queued') state = 'fetching';
        }
        setSourceState(rec, state);
        return;
      }
      decodes.set(role, blob.decoded);
    }
    let source: AssetSource;
    try {
      source = composeSource(rec, decodes);
    } catch (err) {
      setSourceFailed(rec, classify(err));
      return;
    }
    rec.source = source;
    attributeVram(rec);
    setSourceState(rec, 'ready');
  };

  /** Decoded blobs → the `AssetSource` a handle settles on. No I/O and no ownership: a VIEW. */
  const composeSource = (rec: SourceRecord, decodes: Map<string, DecodedBlob>): AssetSource => {
    const need = <T extends DecodedBlob['kind']>(role: string, kind: T): Extract<DecodedBlob, { kind: T }> => {
      const d = decodes.get(role);
      if (d === undefined || d.kind !== kind) throw new MissingError(`no decoded '${role}' (${kind}) blob`);
      return d as Extract<DecodedBlob, { kind: T }>;
    };
    switch (rec.kind) {
      case 'mesh': {
        const g = need('mesh', 'gltf');
        return { kind: 'mesh', scene: g.scene, skinned: g.skinned, tracked: g.tracked };
      }
      case 'body': {
        const g = need('mesh', 'gltf');
        return { kind: 'body', scene: g.scene, rig: need('rig', 'json').value, tracked: g.tracked };
      }
      case 'clip':
        return { kind: 'clip', clips: need('clip', 'clips').clips };
      case 'texture':
        return { kind: 'texture', texture: need('image', 'texture').texture };
      case 'material':
        // Unreachable: `syncSource` returns above for a material. Its source is composed from its
        // resolved texture DEPS (`materialSourceOf`), never from blobs it does not have.
        throw new UnsupportedError("a material is composed from its deps, not from blobs");
      case ENTITY_PREFAB_KIND: {
        /*
         * ⭐ AN ENTITY COMPOSES HERE AND NOT WHERE A MATERIAL DOES, AND THE DIFFERENCE IS WHAT IT
         * WAITS ON. A material waits on records it does not own (its texture assets are the ROOT
         * handle's siblings, so `syncHandle` composes it), which is why it needed its own arm. An
         * entity's content is a CLASS NAME and PARAMS — available the moment the catalog is — and
         * its data assets are ordinary CHILDREN of this handle, so the default `syncHandle` arm
         * already gates on them. It has no blobs, so `rec.blobs` is empty and this runs on the
         * first fold: composed once, immediately, from the catalog.
         */
        const prefab = resolveEntityPrefab(merged, rec.id);
        if (!prefab.found) throw new UnsupportedError(prefab.warnings[0] ?? `entity '${rec.id}' has no prefab`);
        return { kind: 'entity', class: prefab.class, params: prefab.params, data: prefab.data };
      }
      case 'skinned':
      case 'audio':
        throw new UnsupportedError(`kind '${rec.kind}' is not instantiable in this runtime yet`);
    }
  };

  /**
   * The SOURCE RECORD holding a texture asset, by id — how a material reaches its maps.
   *
   * ⚠️ IT LOOKS THE TEXTURE UP IN THE SOURCE TABLE AND NOT IN `link.children`, AND THAT IS NOT A
   * SHORTCUT. A material's dep closure is flattened onto the ROOT handle (§4.2's "no further
   * expansion"), so a material acquired as a dep of another material — which is exactly what an
   * `extends` PARENT is — has no children of its own while its textures are being fetched as the
   * root's siblings. Reading `link.children` there would compose the parent with every slot
   * degraded, permanently, and memoize it. The source table is the level both cases share.
   */
  const textureRecordOf = (texId: string): SourceRecord | undefined => {
    const asset = merged.assets[texId];
    if (asset === undefined) return undefined;
    return sources.get(ledgerKey(texId, asset.hash));
  };

  /**
   * Is every texture this material's chain resolves to SETTLED (ready or failed)? A slot whose
   * asset the catalog does not carry, or which nothing attached, has nothing to wait for and
   * DEGRADES — the fold already reported the dangling reference as a warning.
   */
  const materialDepsSettled = (slots: Readonly<Record<string, string>>): boolean => {
    for (const texId of Object.values(slots)) {
      const rec = textureRecordOf(texId);
      if (rec !== undefined && rec.state !== 'ready' && rec.state !== 'failed') return false;
    }
    return true;
  };

  /**
   * ⭐ COMPOSE A MATERIAL FROM ITS DEPS — the one place the DECLARATION granularity meets the
   * LIFETIME one (plan §13.4). Memoized onto the record, so two handles on one `id@hash` share ONE
   * `makeSurface` call and therefore one material object, one cache key and one program (rule 7).
   *
   * `null` means the chain names no archetype anywhere: there is nothing for the factory to build,
   * and the caller gets `failed('unsupported')` rather than a material that draws as something.
   */
  const materialSourceOf = (rec: SourceRecord): MaterialSource | null => {
    if (rec.source !== null) return rec.source.kind === 'material' ? rec.source : null;
    const resolved = resolveMaterial(merged, rec.id);
    for (const w of resolved.warnings) warnOnce(`material:${rec.id}:${w}`, `[assets] ${w}`);
    const textures: Record<string, TextureSource['texture']> = {};
    for (const [slot, texId] of Object.entries(resolved.textures)) {
      const src = textureRecordOf(texId)?.source;
      if (src !== undefined && src !== null && src.kind === 'texture') textures[slot] = src.texture;
    }
    const built = buildMaterialSource({ id: rec.id, resolved, textures, warnOnce });
    if (built === null) return null;
    holdTextureBlobs(rec, built);
    if (built.degraded.length > 0) {
      // Rule 6: a texture that did not arrive is a STATE, reported once. The archetype's own
      // default stands in its place and the material still draws — never a magenta placeholder.
      warnOnce(
        `material-degraded:${rec.id}`,
        `[assets] material '${rec.id}' has no texture for slot(s) ${built.degraded.join(', ')} — ` +
          `the '${built.archetype}' archetype's default stands in`,
      );
    }
    rec.source = built;
    return built;
  };

  /**
   * ⭐ A COMPOSED MATERIAL HOLDS ITS TEXTURES' BLOBS, BECAUSE A LIFETIME MAY NOT BE A PROPERTY OF
   * THE HANDLE THAT HAPPENED TO ACQUIRE IT (plan §13.0's three granularities, §13.2's walk).
   *
   * `KIND_REQUIRED_ROLES.material` is `[]`, so a material record took NO blob ref of its own — and
   * `materialSourceOf` then memoized `THREE.Texture` objects owned by OTHER records onto
   * `rec.source`. The only thing keeping those objects alive was the ROOT HANDLE's dep closure:
   * the LEDGER count, not this record. So a material sitting idle in its grace period, whose
   * texture is evicted for budget one entry earlier in `evictionOrder`, kept a `Material` bound to
   * a DESTROYED texture — and a re-acquire inside the window settled on the memo, so the freshly
   * refetched texture was bound to nothing and every counter read green (WebGPU:
   * `Destroyed texture … used in a submit`). Whether it fired depended on id ordering, which is
   * exactly the kind of accident this repo does not accept as a fence.
   *
   * The fix is the mechanism that already exists one level down: the BLOB owns the decoded
   * `Texture` and is refcounted by the SOURCES that reference it, so a material takes one ref per
   * bound slot and `discard` releases them with everything else in `rec.blobs`. Consequences,
   * stated: evicting a texture SOURCE whose blob a live material still names now frees nothing
   * (honestly — the bytes are resident because something samples them), a re-acquire of that
   * texture re-uses the decoded object with no fetch at all, and `stats().blobs.refs` counts these
   * references too.
   *
   * ⚠️ IT DOES NOT JOIN `blob.users`. That set is the RE-FOLD fan-out for the asset whose file it
   * is (`setBlobState`, and the failure arm that fails every source over a dead blob); a material
   * composes from settled DEPS and takes its refs only over blobs that are already decoded, so
   * being in it would be a second, wrong answer to "who does this blob's state belong to" — and it
   * would let `attributeVram` charge the texture's MB to the material, which owns none.
   */
  const holdTextureBlobs = (rec: SourceRecord, built: MaterialSource): void => {
    const held = new Set<BlobRecord>(rec.blobs.map((b) => b.blob));
    for (const slot of built.bound) {
      const texId = built.slots[slot];
      if (texId === undefined) continue;
      const texRec = textureRecordOf(texId);
      if (texRec === undefined) continue;
      for (const { blob } of texRec.blobs) {
        if (held.has(blob)) continue;
        held.add(blob);
        blob.refs += 1;
        rec.blobs.push({ role: `texture:${slot}`, blob });
      }
    }
  };

  /** Fold the source's state and the children's into the handle's — the ONE place a handle moves forward. */
  const syncHandle = (ctrl: HandleController<AssetKind>, link: HandleLink): void => {
    const rec = link.record;
    if (rec === null || ctrl.settled || ctrl.released) return;
    if (rec.state === 'failed') {
      ctrl.fail(rec.failure?.code ?? 'network', rec.failure?.message ?? 'load failed');
      return;
    }
    if (rec.kind === MATERIAL_INHERITING_KIND) {
      /*
       * ⭐ A MATERIAL DEGRADES ON A FAILED DEP; EVERY OTHER KIND TAKES ITS DEPENDENCY'S FAILURE
       * (plan §13.4). A body without its rig is not a body, but a material without one of its four
       * maps is the same shader with one fewer bound texture — so the material must settle READY
       * with that slot on the archetype's default rather than fail with it. That is why this arm
       * waits on SETTLEMENT rather than on readiness, and why it never consults `failedChild`.
       */
      const slots = resolveMaterial(merged, rec.id).textures;
      if (!materialDepsSettled(slots)) {
        // Progress is the least-advanced of the textures it is waiting for; the material itself
        // transfers nothing, so it has no progress of its own to report.
        let state: 'queued' | 'fetching' | 'parsing' = 'parsing';
        for (const texId of Object.values(slots)) {
          const t = textureRecordOf(texId);
          if (t === undefined) continue;
          if (t.state === 'queued') state = 'queued';
          else if (t.state === 'fetching' && state !== 'queued') state = 'fetching';
        }
        ctrl.advance(state);
        return;
      }
      const source = materialSourceOf(rec);
      if (source === null) {
        ctrl.fail(
          'unsupported',
          `material '${rec.id}' names no archetype anywhere in its 'extends' chain — the factory has nothing to build`,
        );
        return;
      }
      ctrl.settle(source as SourceOf<AssetKind>);
      return;
    }
    const failedChild = link.children.find((c) => c.handle.state === 'failed' || (c.settled && c.handle.failure !== undefined));
    if (failedChild !== undefined) {
      const f = failedChild.handle.failure;
      ctrl.fail('missing', `dependency '${failedChild.handle.id}' unavailable${f ? ` (${f.code}: ${f.message})` : ''}`);
      return;
    }
    const childrenReady = link.children.every((c) => c.handle.state === 'ready');
    if (rec.state === 'ready' && rec.source !== null && childrenReady) {
      ctrl.settle(rec.source as SourceOf<AssetKind>);
      return;
    }
    // Not settled: the handle's state is the least-advanced of its own source and its children.
    let state: 'queued' | 'fetching' | 'parsing' = rec.state === 'ready' ? 'parsing' : rec.state;
    for (const c of link.children) {
      const cs = c.handle.state;
      if (cs === 'queued') state = 'queued';
      else if (cs === 'fetching' && state !== 'queued') state = 'fetching';
    }
    ctrl.advance(state);
  };

  /*
   * ⚠️ A HANDLE RELEASES AGAINST THE RECORD IT ATTACHED TO, NEVER AGAINST THE KEY.
   *
   * `id@hash` is not a lifetime — it is an ADDRESS, and the same address can name a second source
   * within one session. A blob's failure arm drops the ledger entry and the record while the
   * handles that asked for it are STILL ALIVE (they degrade in place: an `AxonRig` keeps its four
   * failed map handles until `dispose()`). A later acquire of the same id@hash then builds a FRESH
   * entry and a FRESH record, and this time it succeeds — so a stale handle releasing by key would
   * take the NEW entry's count 1 → 0, and `tick()` would dispose a texture the live companion's
   * material is still sampling one grace period later: `Destroyed texture … used in a submit`, with
   * `stats().resident` reading 0 and `onDispose` reporting a healthy expiry. Identity, not the key,
   * is what tells the two apart — `sources.get(key) === rec` is true only while this handle's own
   * record is still the one at that address. When it is not, the entry this handle was counted in
   * was already evicted and credited, so there is nothing left to release. `releaseBlobRef` runs
   * the SAME fence one level down, on `blobs.get(sha256) === blob`.
   *
   * The link is severed FIRST so nothing downstream can re-derive a reference from it.
   */
  const releaseHandle = (ctrl: HandleController<AssetKind>): void => {
    const link = links.get(ctrl);
    if (link === undefined) return;
    links.delete(ctrl);
    for (const child of link.children) child.handle.release();
    link.children = [];
    const rec = link.record;
    const ref = link.ref;
    link.record = null;
    link.ref = null;
    if (rec !== null) rec.handles.delete(ctrl);
    if (rec !== null && ref !== null && sources.get(rec.key) === rec) {
      ledgerRelease(ledger, ref, opts.clock());
      if (rec.handles.size === 0 && rec.state !== 'ready' && rec.state !== 'failed') {
        // Nobody wants it and it is not resident: drop its blob refs now (§4.2), keep nothing. A
        // blob no other source holds aborts its own in-flight fetch inside `discardBlob`.
        ledgerEvict(ledger, ref);
        discard(rec, 'aborted');
        counters.aborted += 1;
      }
    }
    if (!disposed) tick();
  };

  const release = (handle: AssetHandle): void => {
    handle.release();
  };

  /**
   * Take a source out of the table and RELEASE ITS BLOB REFS — the downward half of the walk. The
   * ledger entry is the caller's to remove. `onDispose` fires only for a source that was READY,
   * exactly as it did in P1: an aborted fetch disposed nothing and reporting it as a disposal
   * would double-count every cancelled boot.
   */
  const discard = (rec: SourceRecord, why: DisposedSource['why']): void => {
    if (sources.get(rec.key) === rec) sources.delete(rec.key);
    const held = rec.blobs;
    const wasReady = rec.state === 'ready' && rec.source !== null;
    /*
     * ⭐ THE ONE KIND WHOSE GPU OBJECT IS OWNED BY THE SOURCE RATHER THAN BY A BLOB.
     * §13.2's rule is that the blob owns the decoded resource and the disposal walks down to it —
     * and a material has no blob at all, because its content is not bytes. Its `Material` is minted
     * once per `(id, hash)` by `materialSourceOf` and must be disposed here or it outlives every
     * handle that named it. It is charged NOTHING in the VRAM ledger, deliberately: the bytes it
     * samples are its textures', charged once at their blobs however many materials bind them —
     * which is precisely the saving §13.7 measures.
     */
    if (rec.source !== null && rec.source.kind === 'material') rec.source.material.dispose();
    rec.blobs = [];
    rec.source = null;
    let freed = 0;
    for (const { blob } of held) if (releaseBlobRef(rec, blob, why)) freed += 1;
    if (wasReady) {
      counters.disposed += 1;
      opts.onDispose?.({ id: rec.id, hash: rec.hash, kind: rec.kind, why, blobsFreed: freed });
    }
  };

  // ── fetch pipeline ────────────────────────────────────────────────────────────────────────

  const pump = (): void => {
    if (disposed) return;
    for (const req of drain(scheduler)) {
      const blob = blobs.get(req.id);
      if (blob === undefined || blob.state !== 'queued') {
        schedulerComplete(scheduler, req.id);
        continue;
      }
      void runBlobLoad(blob);
    }
  };

  /** A blob moved: re-fold every source that references it, which re-folds their handles. */
  const setBlobState = (blob: BlobRecord, state: BlobState): void => {
    blob.state = state;
    for (const rec of [...blob.users]) syncSource(rec);
  };

  const setSourceState = (rec: SourceRecord, state: SourceState): void => {
    rec.state = state;
    for (const ctrl of [...rec.handles]) {
      const link = links.get(ctrl);
      if (link !== undefined) syncHandle(ctrl, link);
    }
    /*
     * ⭐ A MATERIAL WAITS ON RECORDS IT DOES NOT OWN, SO SOMETHING HAS TO WAKE IT.
     * Every other kind's readiness travels up its own blobs and its own children; a material's
     * travels through the SOURCE TABLE (`textureRecordOf` — see the note there), which nothing
     * else re-folds. So a texture reaching a settled state re-syncs every material that has not
     * composed yet. It is O(unsettled materials) on a texture settle, which is one walk of a table
     * that holds tens of entries, and it is the alternative to giving a material handle a second,
     * duplicate set of dep handles whose refcounts would double-charge the ledger.
     */
    if (rec.kind === 'texture' && (state === 'ready' || state === 'failed')) {
      for (const other of [...sources.values()]) {
        if (other.kind !== MATERIAL_INHERITING_KIND || other.source !== null) continue;
        for (const ctrl of [...other.handles]) {
          const link = links.get(ctrl);
          if (link !== undefined) syncHandle(ctrl, link);
        }
      }
    }
  };

  const setSourceFailed = (rec: SourceRecord, failure: HandleFailure): void => {
    rec.failure = failure;
    setSourceState(rec, 'failed');
  };

  /** ONE download and ONE decode per blob, however many assets name it (§13.2). */
  const runBlobLoad = async (blob: BlobRecord): Promise<void> => {
    const url = blobUrl(opts.origin, blob.sha256, blob.ext);
    setBlobState(blob, 'fetching');
    let decoded: DecodedBlob | null = null;
    try {
      const res = await opts.fetch(url, { signal: blob.abort.signal });
      if (!res.ok) {
        if (res.status === 404 || res.status === 410) throw new MissingError(`HTTP ${res.status} for ${url}`);
        throw new Error(`HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''} for ${url}`);
      }
      const buf = await res.arrayBuffer();
      if (blob.abort.signal.aborted) throw new AbortedError('aborted');
      if (verify) {
        const got = await verifier(buf);
        if (got !== blob.sha256) {
          throw new Error(`integrity: blob sha256 ${got.slice(0, 12)}… does not match the catalog's ${blob.sha256.slice(0, 12)}…`);
        }
      }
      setBlobState(blob, 'decoding');
      decoded = await blob.decode(buf);
      if (blob.abort.signal.aborted || blobs.get(blob.sha256) !== blob) {
        // Released (or evicted) while decoding: the bytes were wasted, the objects must not leak.
        disposeDecoded(decoded);
        throw new AbortedError('aborted');
      }
      const measuredMB = measureDecoded(decoded) / MiB;
      opts.vram?.credit(blob.owner);
      opts.vram?.charge(blob.owner, measuredMB * MiB);
      blob.vramMB = measuredMB;
      blob.decoded = decoded;
      for (const rec of blob.users) attributeVram(rec);
      setBlobState(blob, 'ready');
    } catch (err) {
      const failure = classify(err);
      if (blobs.get(blob.sha256) !== blob) return; // already discarded by release/evict — nothing to report
      blob.failure = failure;
      if (failure.code !== 'aborted') {
        counters.blobsFailed += 1;
        counters.failed += 1;
        warnOnce(
          `${failure.code}:${blob.sha256}`,
          `[assets] blob ${blob.sha256.slice(0, 12)}….${blob.ext} ${failure.code}: ${failure.message}`,
        );
      }
      /*
       * A failed blob is not resident: forget it so a later acquire retries, and free the charge.
       * Its users keep their failure AND their reference to THIS record — which is what makes
       * their later release a no-op against a FRESH blob at the same address (`releaseBlobRef`:
       * identity, not the key). The same is done one level up for the sources it fails, and for
       * the same reason: dropping either without that fence is a use-after-free on the GPU one
       * grace period after the retry succeeds.
       */
      blobs.delete(blob.sha256);
      opts.vram?.credit(blob.owner);
      blob.vramMB = 0;
      blob.state = 'failed';
      for (const rec of [...blob.users]) {
        setSourceFailed(rec, failure);
        if (sources.get(rec.key) === rec) {
          sources.delete(rec.key);
          ledgerEvict(ledger, { id: rec.id, hash: rec.hash });
          const held = rec.blobs;
          rec.blobs = [];
          for (const { blob: other } of held) if (other !== blob) releaseBlobRef(rec, other, 'aborted');
        }
      }
    } finally {
      schedulerComplete(scheduler, blob.sha256);
      pump();
    }
  };

  // ── lifetime ──────────────────────────────────────────────────────────────────────────────

  const tick = (): number => {
    if (disposed) return 0;
    let n = 0;
    for (const e of expire(ledger, opts.clock())) {
      const rec = sources.get(e.key);
      if (rec !== undefined) discard(rec, 'expired');
      n += 1;
    }
    prune(scheduler);
    return n;
  };

  /** §2.3's `lib:` arm. Pure lookup over the merged catalog + `uri.blobUrl`; never fetches. */
  const fileUrl = (id: string, role?: string): string | null => {
    const asset = merged.assets[id];
    if (asset === undefined) return null;
    const want = role ?? rolesFor(asset.kind)[0];
    if (want === undefined) return null;
    const file = asset.files[want];
    if (file === undefined) return null;
    return blobUrl(opts.origin, file.sha256, file.ext);
  };

  const blobStats = (): BlobStats => {
    let resident = 0;
    let inflight = 0;
    let refs = 0;
    let mb = 0;
    for (const b of blobs.values()) {
      resident += 1;
      refs += b.refs;
      mb += b.vramMB;
      if (b.state !== 'ready' && b.state !== 'failed') inflight += 1;
    }
    return {
      resident,
      inflight,
      mb,
      refs,
      shared: counters.blobsShared,
      disposed: counters.blobsDisposed,
      failed: counters.blobsFailed,
    };
  };

  const stats = (): LibraryStats => {
    const totals = ledgerTotals(ledger);
    const sc = schedulerCounts(scheduler);
    let inflight = 0;
    for (const rec of sources.values()) if (rec.state !== 'ready' && rec.state !== 'failed') inflight += 1;
    const bs = blobStats();
    return {
      resident: totals.resident,
      inflight,
      // ⭐ SUMMED OVER BLOBS, not over the ledger's per-asset attribution: two assets sharing one
      // upload are two residents and ONE charge, and this is the number that says so.
      mb: bs.mb,
      evictions: counters.evictions,
      disposed: counters.disposed,
      aborted: counters.aborted,
      failed: counters.failed,
      byLane: totals.byLane,
      scheduler: { queued: sc.queued, active: sc.active },
      handles: handleCounters(views()),
      blobs: bs,
      catalog: { hash: primaryHash, assets: Object.keys(merged.assets).length, secondaries: secondaries.length },
    };
  };

  const disposeAll = (): void => {
    if (disposed) return;
    disposed = true;
    for (const ctrl of [...links.keys()]) {
      links.delete(ctrl);
      ctrl.markReleased();
    }
    for (const rec of [...sources.values()]) discard(rec, 'dispose');
    sources.clear();
    for (const blob of [...blobs.values()]) discardBlob(blob, 'dispose');
    blobs.clear();
    for (const e of Object.values(ledger.entries)) ledgerEvict(ledger, e);
    prune(scheduler);
  };

  return {
    loadCatalog,
    addCatalog,
    useCatalog,
    catalog: () => merged,
    fileUrl,
    acquire,
    release,
    tick,
    stats,
    handles: views,
    bootGate: () => bootGate(views()),
    dispose: disposeAll,
  };
}
